// Shared by the password-gated routes (/secret-event, /private-sale) and, for the
// indexing half only, by /private-sale-sign-up.

/**
 * The indexing directive alone, for `/private-sale-sign-up` — a public page that
 * must stay unindexed but has no cookie-specific content, so `no-store` would cost
 * it the back/forward cache for nothing.
 */
export const NOINDEX_HEADER = { 'X-Robots-Tag': 'noindex, nofollow' } as const;

import { Routes } from './routes';

/**
 * The routes of every password-gated campaign, and the source of `CampaignKey`: the
 * gate + grid at `gate`, product pages at `${gate}/:handle`, the email-capture page at
 * `signUp` posting to `signUpApi`. Lives here, not in `constants/campaigns.ts`, because
 * the layout and robots read it and must not pull that file's artwork imports into
 * their chunks. Every other campaign table is `Record<CampaignKey, …>`, so a campaign
 * added here without its other halves is a type error. See `specs/gated-campaigns.md`.
 */
export const CAMPAIGN_PATHS = {
  privateSale: {
    gate: Routes.PrivateSale,
    signUp: Routes.PrivateSaleSignUp,
    signUpApi: '/api/private-sale-sign-up',
  },
  euLaunch: {
    gate: Routes.EuLaunch,
    signUp: Routes.EuSignUp,
    signUpApi: '/api/eu-sign-up',
  },
} as const;

export type CampaignKey = keyof typeof CAMPAIGN_PATHS;

export const CAMPAIGN_KEYS = Object.keys(CAMPAIGN_PATHS) as CampaignKey[];

/**
 * EU launch early access (regions still off site-wide): the public shopping pages are
 * UK; everything else (`null`: the launch pages, the cart, policies, API routes) keeps
 * the store the shopper picked, so they can shop the launch in any region and reading
 * /privacy cannot wipe an EU bag. Server and client both read this, so they cannot
 * disagree.
 */
export const earlyAccessPathRegion = (pathname: string): 'UK' | null => {
  if (
    pathname === Routes.Home ||
    pathname.startsWith(Routes.Shop) ||
    pathname.startsWith(`${Routes.Product}/`)
  )
    return 'UK';
  return null;
};

export const CAMPAIGN_GATE_PATHS = CAMPAIGN_KEYS.map(
  (key) => CAMPAIGN_PATHS[key].gate,
);

export const CAMPAIGN_SIGN_UP_PATHS = CAMPAIGN_KEYS.map(
  (key) => CAMPAIGN_PATHS[key].signUp,
);

/**
 * Every path prefix behind a password gate. One list so a third gated route does not
 * have to be remembered in each place that asks the question below.
 */
export const GATED_ROUTE_PREFIXES = [
  ...CAMPAIGN_GATE_PATHS,
  Routes.SecretEvent,
] as const;

/**
 * Whether a surface may be disclosed to a third party (analytics, ESP, sitemap).
 *
 * Deliberately path-based rather than tag-based: `isGatedProduct` covers every
 * campaign tag, but the secret-event product is gated by handle and carries no
 * tag at all, so a tag check alone fails open on it.
 */
export const isPubliclyAttributableSurface = (pathname: string): boolean =>
  !GATED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Applied to every response from a gated route, including 404s and 500s.
// `no-store` matters because the HTML is cookie-specific and must never be
// edge-cached.
export const NOINDEX_HEADERS = {
  ...NOINDEX_HEADER,
  'Cache-Control': 'no-store',
};

// Hidden field bots tend to fill in. Lives here rather than next to the form so
// server-only modules can read it without pulling React into their import graph.
export const HONEYPOT_FIELD = 'company_website';

/**
 * Route-level `headers` for gated routes, so NOINDEX_HEADERS also apply to
 * responses the loader/action never shapes itself — notably an ErrorBoundary
 * render, where a thrown Response's own headers are not carried through.
 *
 * `Set-Cookie` is preserved explicitly: the password action issues the access
 * cookie that way, and a bare `headers` export would otherwise drop it.
 */
export const buildGatedRouteHeaders = ({
  actionHeaders,
  loaderHeaders,
}: {
  actionHeaders?: Headers;
  loaderHeaders?: Headers;
}): Headers => {
  const headers = new Headers(NOINDEX_HEADERS);

  const setCookie =
    actionHeaders?.get('Set-Cookie') ?? loaderHeaders?.get('Set-Cookie');
  if (setCookie) headers.set('Set-Cookie', setCookie);

  return headers;
};
